import React from 'react';
import _ from 'lodash';
import { isBoolean } from 'lodash';
import { Stack, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { Chip, Typography as CprimeTypography } from 'cprime-foundational-components';
import PrintChip from 'components/PrintChip/PrintChip';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import { getStatusChipColor } from 'utils/statusUtils';
import { formatDataValue } from 'utils/formatFns';
import theme from 'theme';

const PAPER_COLOR = theme.palette.background.surfacePaper;
const WHITE_TEXT_COLOR = theme.palette.text.white;
const PRIMARY_TEXT_COLOR = theme.palette.primary.main;

const usdFormatter = (data) => {
  if (!data) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(data);
};

export const nsoSpecialOrderFormat = (specialOrderType, specialOrderIndicator) => {
  if (specialOrderType === 'NSO' && isBoolean(specialOrderIndicator)) {
    if (specialOrderIndicator) {
      return 'Y';
    }
    return 'N';
  }

  return 'N';
};

const tooltipContent = (data) => (
  <CprimeTypography content="span" variant="label" px={2} pt={2} pb={1.125}>
    {data}
  </CprimeTypography>
);

export const getVipChips = (data, isPrint) => {
  if (!data || data === 'N/A') {
    return (
      <Typography
        component="span"
        variant={isPrint ? 'print2' : 'body2'}
        width="60%"
        whiteSpace="nowrap"
      >
        N/A
      </Typography>
    );
  }
  const chips = _.compact(data?.split(','));
  return (
    <Stack height="100%" justifyContent="center" width="60%">
      <Stack direction="row" alignItems="center" spacing={1.25}>
        {chips?.map((chip) => (
          <TooltipWrapper key={chip} tooltipText={tooltipContent(chip)}>
            <div>
              <Chip
                color={PAPER_COLOR}
                backgroundColor={theme.palette.purple[10]}
                label={isPrint ? chip : chip[0]}
              />
            </div>
          </TooltipWrapper>
        ))}
      </Stack>
    </Stack>
  );
};

export const getEtaStatusChip = (data, isPrint, searchContextValues) => {
  if (!data || data === 'N/A') {
    return (
      <TypographyMarked
        component="span"
        variant={isPrint ? 'print2' : 'label'}
        width="60%"
        whiteSpace="nowrap"
        value="N/A"
        fieldId="customerETAStatusNA"
        searchContextValues={searchContextValues}
      />
    );
  }

  const etaUnavailable = 'ETA Unavailable';

  return isPrint ? (
    <PrintChip
      label={data}
      color={data === etaUnavailable ? PRIMARY_TEXT_COLOR : WHITE_TEXT_COLOR}
      backgroundColor={data === etaUnavailable ? PAPER_COLOR : getStatusChipColor(data)}
      outlined
    />
  ) : (
    <Chip
      icon={<PersonIcon color={data === etaUnavailable ? PRIMARY_TEXT_COLOR : WHITE_TEXT_COLOR} />}
      variant={data === etaUnavailable ? 'outlined' : 'filled'}
      color={data === etaUnavailable ? PRIMARY_TEXT_COLOR : WHITE_TEXT_COLOR}
      backgroundColor={data === etaUnavailable ? PAPER_COLOR : getStatusChipColor(data)}
      label={
        <TypographyMarked
          value={data}
          variant="label"
          fieldId={`customerETAStatusNA-${data}`}
          searchContextValues={searchContextValues}
          sx={{ fontSize: '0.75rem' }}
        />
      }
      sx={{
        '&.MuiChip-outlined': {
          borderColor: '#bdbdbd',
        },
      }}
    />
  );
};

export const nationalSoldOrders = ({ vehicle, isPrint, searchContextValues }) =>
  vehicle?.soldOrder?.soldOrderIndicator
    ? {
        soldOrderHero: [
          {
            label: 'Sold Order No.',
            value: formatDataValue({
              value: vehicle?.soldOrder?.soldOrderNumber,
            }),
          },
          {
            label: 'Sold Order Date',
            value: formatDataValue({
              value: vehicle?.soldOrder?.soldOrderCreationDateFmt,
            }),
          },
          {
            label: 'Submitted Date',
            value: formatDataValue({
              value: vehicle?.soldOrder?.soldOrderSubmittedDateFmt,
            }),
          },
          {
            label: 'Status',
            value: formatDataValue({
              value: vehicle?.soldOrder?.soldOrderStatus,
            }),
          },
        ],
        soldOrderDetails: [
          {
            label: 'Customer ETA Status',
            value: getEtaStatusChip(
              vehicle?.vehicleTracking?.swCustomerEtaStatus,
              isPrint,
              searchContextValues,
            ),
          },
          {
            label: 'Customer Initial ETA Window',
            value: `${formatDataValue({
              value: vehicle?.vehicleTracking?.customerOrgEtaWindowStartFmt,
            })} - ${formatDataValue({
              value: vehicle?.vehicleTracking?.customerOrgEtaWindowEndFmt,
            })}`,
          },
          {
            label: 'Dealer',
            value: `${formatDataValue({
              value: vehicle?.soldOrder?.soldOrderDealerCode,
            })} | ${formatDataValue({
              value: vehicle?.soldOrder?.soldOrderDealerName,
            })}`,
          },
          {
            label: 'VIP',
            value: getVipChips(
              `${vehicle?.soldOrder?.vipHqSelectedIndicator ? 'National,' : ''}${
                vehicle?.soldOrder?.vipRegionSelectedIndicator ? 'Region,' : ''
              }${vehicle?.soldOrder?.vipDealerSelectedIndicator ? 'Dealer' : ''}`,
              isPrint,
            ),
          },
        ],
        vehicleDetails: [
          {
            label: 'TSRP',
            value: usdFormatter(vehicle?.pricing?.dealerMSRP?.totalSuggestedRetailPrice),
          },

          {
            label: 'TSRP Presented',
            value: usdFormatter(vehicle?.soldOrder?.tsrpPresented),
          },
          {
            label: 'NSO Special Order',
            value: nsoSpecialOrderFormat(
              vehicle?.orderInfo?.specialOrderType,
              vehicle?.orderInfo?.specialOrderIndicator,
            ),
          },
          {
            label: 'Fulfillment Type',
            value: formatDataValue({
              value: vehicle?.soldOrder?.fulfillmentTypeDescription,
            }),
          },
        ],
      }
    : null;
